import { httpClient } from './httpClient';
import { User } from '../types/user';

export interface CreateUserData {
  full_name: string;
  phone: string;
  email: string;
  role?: string;
  department_id?: number;
  manager_id?: number;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export const userService = {
  /**
   * Search users by name or email
   * @param query Search query string
   * @param page Page number (default 1)
   * @param limit Number of results per page (default 20)
   * @returns Array of matching users
   */
  async searchUsers(query: string, page: number = 1, limit: number = 20): Promise<User[]> {
    try {
      const response = await httpClient.get<User[]>(
        `/api/users/search?query=${encodeURIComponent(query)}&page=${page}&limit=${limit}`
      );
      return response;
    } catch (error) {
      console.error('Error searching users:', error);
      // Return empty array instead of throwing error
      return [];
    }
  },
  async getUsers(): Promise<User[]> {
    try {
      const response = await httpClient.get<User[]>('/api/users');
      return response;
    } catch (error) {
      console.error('Error fetching users:', error);
      // Return empty array instead of throwing error
      return [];
    }
  },

  async getUser(id: number): Promise<User> {
    const response = await httpClient.get<User>(`/api/users/${id}`);
    return response;
  },

  async createUser(data: CreateUserData): Promise<User> {
    const requestData = {
      full_name: data.full_name.trim(),
      phone: data.phone.trim(),
      email: data.email.trim(),
      role: data.role || 'agent',
      department_id: data.department_id,
      manager_id: data.manager_id,
      useAutoGeneratedPassword: true
    };
    const response = await httpClient.post<User>('/api/users', requestData);
    return response;
  },

  async updateUser(id: number, data: Partial<CreateUserData>): Promise<User> {
    const response = await httpClient.put<User>(`/api/users/${id}`, data);
    return response;
  },

  async deleteUser(id: number): Promise<{ message: string; user?: User }> {
    const response = await httpClient.delete<{ message: string; user?: User }>(`/api/users/${id}`);
    return response;
  },

  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const response = await httpClient.post<{ message: string }>('/api/users/change-password', data);
    return response;
  },

  async resetPassword(userId: number): Promise<{ message: string; tempPassword: string }> {
    const response = await httpClient.post<{ message: string; tempPassword: string }>(
      `/api/users/${userId}/reset-password`
    );
    return response;
  },

  async forgotPassword(email: string, subdomain: string): Promise<{ message: string }> {
    const response = await httpClient.post<{ message: string; tempPassword: string }>(
      `/api/users/forgot-password`,
      { email: email, subdomain: subdomain }
    );
    return response;
  },

  async verifyResetPasswordRequest(
    token: string
  ): Promise<{ message: string; tempPassword: string }> {
    const response = await httpClient.post<{ message: string; tempPassword: string }>(
      `/api/users/verify-reset-password-request`,
      { token: token }
    );
    return response;
  },

  async verifyEmail(token: string): Promise<{
    success: boolean;
    message: string;
    tenant?: { id: string; name: string; verified: boolean };
  }> {
    const response = await httpClient.post<{
      success: boolean;
      message: string;
      tenant?: { id: string; name: string; verified: boolean };
    }>(`/api/users/verify-email`, { token: token });
    return response;
  },

  /**
   * Upload a profile picture for the current user
   * @param file The image file to upload
   * @returns The updated user data with the new profile picture URL
   */
  async uploadProfilePicture(file: File): Promise<{ pfp_url: string; user: User }> {
    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await httpClient.post<{ pfp_url: string; user: User }>(
        '/api/users/profile-picture',
        formData
      );
      return response;
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      throw new Error(
        error instanceof Error
          ? `Failed to upload profile picture: ${error.message}`
          : 'Failed to upload profile picture'
      );
    }
  },

  async getProfile(): Promise<User> {
    const response = await httpClient.get<User>('/api/users/profile');
    return response;
  },

  async updateProfile(_data: Partial<User>): Promise<User> {
    // We need to handle the case where we're updating the current user's profile
    // The backend endpoint for updating the current user might be different or we reuse updateUser
    // Assuming we can use updateUser with the current user's ID, but we don't have it here easily without auth context.
    // However, usually there's a specific endpoint for "me".
    // Let's assume we use the existing updateUser but we need the ID.
    // Alternatively, if there is a /api/users/profile PUT endpoint.
    // Based on backend routes, we have router.put('/:id', ...).
    // Let's check if we have a profile update endpoint.
    // Looking at userRoutes.ts, we have router.get('/profile', ...).
    // We don't seem to have a specific PUT /profile.
    // But we can use the ID from the profile we just fetched if we are in a component.
    // But here we are in a service.
    // Let's assume the component will pass the ID to updateUser, OR we implement a new endpoint.
    // For now, let's implement updateProfile to take an ID, or we rely on the component to call updateUser.
    // Wait, OrganizationSection calls userService.updateProfile({ department_id }).
    // It expects to just pass data.
    // I should probably add a PUT /api/users/profile endpoint in the backend or change the frontend to use updateUser(id, data).
    // Changing frontend to use updateUser(id, data) is safer as I don't want to touch backend again if possible.
    // But OrganizationSection doesn't have the ID easily available when defining the handler? 
    // It does have `profile.id`.
    // So I will change OrganizationSection to use userService.updateUser(profile.id, data).
    // But for now I will add a placeholder or just remove the call from OrganizationSection and use updateUser there.
    // Actually, I'll just add getProfile here.
    return Promise.reject('Use updateUser(id, data) instead');
  },

  async requestRelationship(targetId: number, type: 'manager' | 'subordinate'): Promise<any> {
    const response = await httpClient.post('/api/users/relationships/request', { targetId, type });
    return response;
  },

  async respondToRelationshipRequest(requestId: number, status: 'accepted' | 'rejected'): Promise<any> {
    const response = await httpClient.put(`/api/users/relationships/${requestId}/respond`, { status });
    return response;
  },

  async getRelationshipRequests(): Promise<{ incoming: any[]; outgoing: any[] }> {
    const response = await httpClient.get<{ incoming: any[]; outgoing: any[] }>('/api/users/relationships');
    return response;
  },

  async getSubordinates(): Promise<User[]> {
    const response = await httpClient.get<User[]>('/api/users/subordinates');
    return response;
  }
};
